import HttpService from './HttpService';
import type { DurableSkillsReportDTO } from '@/schemas/DurableSkillsReport';

/**
 * Fetches the Durable Skills Report
 */

// How long a cached DSR is trusted before a re-fetch is forced.
const DSR_CACHE_TTL_MS = 10 * 60 * 1000;

interface CachedReport {
  promise: Promise<any>;
  fetchedAt: number;
}

const DSRCache = new Map<number, CachedReport>();
export const fetchReportResponse = (userId: number): Promise<any> => {
  const cached = DSRCache.get(userId);
  if (!cached || Date.now() - cached.fetchedAt > DSR_CACHE_TTL_MS) {
    const promise = HttpService.get(`/durable-skills-report/${userId}/`).catch((error) => {
      DSRCache.delete(userId);
      throw error;
    });
    DSRCache.set(userId, { promise, fetchedAt: Date.now() });
  }
  return DSRCache.get(userId)!.promise;
};

export async function fetchDurableSkillsReport(userId: number): Promise<DurableSkillsReportDTO[] | null> {
  try {
    // Uses the cached promise
    const response = await fetchReportResponse(userId);
    const durableSkillsReportData = response.data;

    if (durableSkillsReportData && Array.isArray(durableSkillsReportData.durableSkillsReports) && durableSkillsReportData.durableSkillsReports.length > 0) {
      const reports = durableSkillsReportData.durableSkillsReports as DurableSkillsReportDTO[];
      const skillsReport = reports.filter((report) => report && report.skills_report_status != null && report.skills_report_status !== 'missing');
      if (skillsReport.length > 0) return skillsReport;
      // None of the returned reports contain a skills_report we can render
      return null;
    }

    return null;
  } catch (error: any) {
    console.warn('Unable to fetch DSR Report:', error);
    throw error;
  }
}

export async function isDurableSkillsReportPilotParticipant(userId: number): Promise<boolean> {
  try {
    const response = await fetchReportResponse(userId);
    return response.status === 200;
  } catch (error: any) {
    console.warn('Unable to determine DSR pilot participation:', error);
    return false;
  }
}

export function clearDurableSkillsReportCache(userId?: number): void {
  if (userId !== undefined) {
    DSRCache.delete(userId);
  } else {
    DSRCache.clear();
  }
}